import * as React from 'react';
import {
    Chart,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartValueAxis,
    ChartValueAxisItem, ChartArea
} from '@progress/kendo-react-charts';
import PropTypes from 'prop-types';

const EnergyProjectHoursByVerticalChart = React.memo(({ energyProjectHoursByVerticalData, chartRef }) => {
    const chartData = energyProjectHoursByVerticalData?.ChartData ?? [];

    // Categories (X-axis) come from each entry's Date
    const categories = chartData.map(item => item.Date);

    // Derive unique sector names from data, preserving preferred order
    const sectorOrder = ["Upstream", "Midstream", "Richardson-Barr", "LNG", "Energy Transition", "Admin and One-Offs"];
    const sectorNamesInData = [...new Set(chartData.flatMap(item => (item.Sectors ?? []).map(s => s.SectorName)))];
    const sectors = [
        ...sectorOrder.filter(s => sectorNamesInData.includes(s)),
        ...sectorNamesInData.filter(s => !sectorOrder.includes(s)),
    ];

    // For each sector, build a data array across all dates (0 if missing for a date)
    const getSeriesData = (sectorName) =>
        chartData.map(item => (item.Sectors ?? []).find(s => s.SectorName === sectorName)?.Hours ?? 0);
    const colors = ["#002750", "#73b0e3", "#8499a6", "#b8a970", "#c1b5a5","#0051A5","#707A78", "#906646","#6B702B","#AABA0A","#FEDF01"]; // Extended color palette

    const axisLabelStyle = {
        color: '#424242',
        font: '12px Segoe UI, sans-serif',
    };
    const baseLabels = {
        visible: true,
        font: '10px Segoe UI, sans-serif',
        color: '#fff',
        opacity: 1
    };

    return (
        <Chart style={{ height: 500 }} ref={chartRef}>
            <ChartArea margin={20} />
            <ChartLegend position="bottom"
                item={{ visual: undefined, cursor: 'default', highlight: { visible: false } }}
                orientation="horizontal"
                labels={{ font: '12px Segoe UI, sans-serif', color: '#323130' }} />
            <ChartValueAxis>
                <ChartValueAxisItem
                    title={{
                        text: 'Hours',
                        font: '12px bold Segoe UI, sans-serif',
                        color: '#323130',
                    }}
                    labels={{ format: '{0}' }}
                />
            </ChartValueAxis>
            <ChartCategoryAxis>
                <ChartCategoryAxisItem majorGridLines={{ visible: false }} labels={axisLabelStyle} categories={categories} />
            </ChartCategoryAxis>

            <ChartSeries>
                {sectors.map((sector, idx) => (
                    <ChartSeriesItem
                        key={sector}
                        labels={{ ...baseLabels, background: colors[idx % colors.length] }}
                        color={colors[idx % colors.length]}
                        type="line"
                        data={getSeriesData(sector)}
                        name={sector}
                        legendItem={{ markers: { visible: false }, type: 'area' }}
                        tooltip={{ visible: true }}
                    />
                ))}
            </ChartSeries>
        </Chart>
    );
});

EnergyProjectHoursByVerticalChart.propTypes = {
    energyProjectHoursByVerticalData: PropTypes.shape({
        ChartData: PropTypes.arrayOf(PropTypes.shape({
            Date: PropTypes.string.isRequired,
            Sectors: PropTypes.arrayOf(PropTypes.shape({
                SectorName: PropTypes.string.isRequired,
                Hours: PropTypes.number.isRequired,
            }))
        }))
    }),
    chartRef: PropTypes.object,
};

export default EnergyProjectHoursByVerticalChart;